import { Empty, Spin } from 'antd';
import isArray from 'lodash/isArray';
import dayjs from 'dayjs';
import Image from 'next/image';
import React, { Fragment, MouseEventHandler, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { If } from '@components/condition';
import { ButtonCopy } from '@components/shared';
import { CircleImage } from '@components/shared/images/CircleImage';
import { locationSelector } from '@store/location/location.slice';
import { EOrderStatus, IOrder } from '@type/order';
import { EUnitDisplay } from '@type/product';
import { findOrderStatus, splitText } from '@utils/function';
import { formatVND } from '@utils/number';

interface IProps {
  data: IOrder[];
  handleClickEdit: (order: IOrder) => void;
  loading?: boolean;
}

function OrderTable({ data, handleClickEdit, loading }: IProps) {
  const { locations } = useSelector(locationSelector);

  return (
    <Spin spinning={loading}>
      <div
        id="custom-scroll"
        className="my-3 w-full overflow-auto border border-gray-200 rounded-lg"
      >
        <ContainerRow className="bg-[#fafafa] h-16 rounded-lg uppercase items-center text-sm font-bold text-[#1d2939] border-b border-gray-200">
          <div>thông tin đơn hàng</div>
          <div className="text-center">Số lượng</div>
          <div className="text-center">Thành tiền</div>
          <div className="text-center">Sl hoàn trả</div>
          <div className="text-center">Số tiền hoàn lại</div>
          <div className="pr-2 text-end">Trạng thái</div>
        </ContainerRow>
        <If
          condition={data?.length}
          Then={data?.map((order) => {
            return (
              <ContainerRow
                className="cursor-pointer border-b border-gray_200"
                key={order._id}
                onClick={() => handleClickEdit(order)}
              >
                <div className="col-span-6 text-sm font-medium text-secondary flex items-center justify-between gap-3 border-b border-dashed border-gray_200 pb-3">
                  <div className="flex items-center">
                    <div className="flex items-center gap-2">
                      <CircleImage
                        className="mr-1 w-10 h-10 rounded-full border border-gray_200 object-conver"
                        imageUrl={order?.user?.avatar}
                        name={order?.user?.name}
                      />
                      <div>
                        <div className="flex items-center">
                          <div className="mr-2 font-bold">{order?.user?.name}</div>
                          <div className="font-normal text-gray_500">
                            ({order?.items?.length} sản phẩm)
                          </div>
                        </div>
                        <div className="flex gap-1 text-sm">
                          <div className="text-gray_500">Mã đơn hàng:</div>
                          <ButtonCopy text={order?._id} className="text-sm">
                            <div className="text-semantic_2 font-medium font-mono">
                              {splitText(order?._id, 5)}
                            </div>{' '}
                          </ButtonCopy>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end">
                    <div className="font-medium text-secondary flex items-center mr-2">
                      <span className="mr-1">Ngày tạo:</span>
                      {dayjs(order?.createdAt).format('HH:mm DD/MM/YYYY')}
                    </div>
                    <div className="text-sm">
                      {locations.getOrderAddress(order?.shippingAddress)}
                    </div>
                  </div>
                </div>
                <OrderRow order={order} />
              </ContainerRow>
            );
          })}
          Else={
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Chưa có đơn hàng nào"
              className="py-6"
            />
          }
        />
      </div>
    </Spin>
  );
}

export default OrderTable;

const OrderRow = ({ order }: { order: IOrder }) => {
  const items = isArray(order?.items) ? order.items : [];
  const [visibleItems, setVisibleItems] = useState(3);

  const currentStatus = findOrderStatus(order?.status);

  const handleShowMore: MouseEventHandler = (e) => {
    e.stopPropagation();
    setVisibleItems((prev) => Math.min(prev + 3, items.length));
  };

  const handleShowLess: MouseEventHandler = (e) => {
    e.stopPropagation();
    setVisibleItems(3);
  };

  return (
    <Fragment>
      {items?.slice(0, visibleItems)?.map((product, i) => (
        <Fragment key={product?.productId}>
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0">
              <div className="relative w-9 h-9 overflow-hidden">
                <Image
                  className="w-9 h-9 object-cover rounded"
                  src={product.images?.[0] || '/img/image_empty.svg'}
                  alt=""
                  fill
                />
              </div>
            </div>
            <div className="font-medium flex flex-col justify-center">
              <div className="text-secondary line-clamp-2 font-semibold text-sm">
                {product?.name}
              </div>
              <ButtonCopy text={product?.code}>
                <span className="text-xs font-mono">Id:{product?.code}</span>
              </ButtonCopy>
            </div>
          </div>
          <div className="font-semibold text-secondary flex gap-1 items-center justify-center text-center text-green-700">
            {product?.quantity} <span className="text-sm">{EUnitDisplay[product?.unitName]}</span>
          </div>

          <div className="font-semibold text-secondary flex items-center justify-center gap-1">
            {formatVND(
              order?.isTax
                ? product?.price * product?.quantity +
                    (product?.price * product?.quantity * product?.tax) / 100
                : product?.price * product?.quantity || 0
            )}
            <span className="text-sm">đ</span>
          </div>

          <div className="font-semibold text-secondary flex items-center justify-center text-red-500 gap-1">
            {product?.damagedQuantity}
            <span className="text-sm">{EUnitDisplay[product?.unitName]}</span>
          </div>

          <div className="font-semibold text-secondary flex items-center gap-1 justify-center text-red-500">
            {formatVND(product?.refundAmount || 0)} <span className="text-sm">đ</span>
          </div>

          <div className="flex items-center justify-end">
            <If
              condition={currentStatus && !i}
              Then={
                <div className="inline-flex items-center">
                  <div className="font-bold text-xs flex-1 text-end">
                    <div className="typography-h5" style={{ color: currentStatus?.color }}>
                      {currentStatus?.label || 'Không xác định'}
                    </div>
                    <If
                      condition={currentStatus?.key === EOrderStatus.DEBT}
                      Then={
                        <div className="text-gray_500 font-medium text-red-500">
                          Số tiền {formatVND(order?.unPaidAmount || 0)}đ
                        </div>
                      }
                    />
                    <If
                      condition={currentStatus?.key === EOrderStatus.CANCELED}
                      Then={<div className="text-gray_500 font-medium">Được huỷ bởi user</div>}
                    />
                  </div>
                </div>
              }
            />
          </div>
        </Fragment>
      ))}

      <div className="text-center col-span-6">
        <If
          condition={visibleItems < order?.items?.length}
          Then={
            <button
              className="inline-flex items-center gap-1 text-gray_500 text-sm font-medium"
              onClick={handleShowMore}
            >
              <div>Xem thêm</div>
              <img src="/icons/double_arrow_down.svg" alt="" />
            </button>
          }
          Else={
            <If
              condition={visibleItems > 3}
              Then={
                <button
                  className="inline-flex items-center gap-1 text-gray_500 text-sm font-medium"
                  onClick={handleShowLess}
                >
                  <div>Thu gọn</div>
                  <img src="/icons/double_arrow_up.svg" alt="" />
                </button>
              }
            />
          }
        />
      </div>
    </Fragment>
  );
};

const ContainerRow = styled.div`
  display: grid;
  grid-template-columns:
    minmax(200px, 2fr) /* Cột 1: ưu tiên rộng hơn */
    minmax(100px, 1fr) /* Cột 2 */
    minmax(150px, 1.2fr) /* Cột 3 */
    minmax(150px, 1.2fr) /* Cột 4 */
    minmax(150px, 1.2fr) /* Cột 5 */
    minmax(150px, 1.2fr); /* Cột 6 */
  padding: 8px 16px;
  gap: 8px;
  min-width: 1000px;
`;
